import { IChatServerService, ServerServiceSubscriptionStore, IServerServiceFactory } from '@/app-modules/server-service';
import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { User } from '@/app-modules/store/modules/user';
import { IConfigurationService, ConfigurationKey, ConfigurationPaths, IChatModsConfiguration } from '@/app-modules/config';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { ArcadeWinnerMessageResponse, ArcadeWinnerReceivedEventName } from '@/components/chat/_domain';

interface IArcadeAdminFeedServiceProvider {
  initialise(): Promise<void>;
  getAdminFeedService(): IChatServerService;
  dispose(): void;
}

@injectable()
class ArcadeAdminFeedServiceProvider implements IArcadeAdminFeedServiceProvider {
  private subscriptionStore: ServerServiceSubscriptionStore;
  private arcadeChatServerService: IChatServerService;
  private chatModConfig: IChatModsConfiguration;

  constructor(
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) private readonly serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IPersistedStore) private readonly persistedStore: IPersistedStore,
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IEventBusService) private readonly eventBusService: IEventBusService
  ) {
  }

  public async initialise(): Promise<void> {
    this.chatModConfig = await this.configurationService.getConfiguration();
    if (this.arcadeChatServerService) {
      return;
    }

    await this.initialiseArcadeChatServerService();
    this.subscriptionStore = new ServerServiceSubscriptionStore(
      this.arcadeChatServerService.subscribeConnected(this.connectAndAuthToChat.bind(this)),
      this.arcadeChatServerService.subscribeArcadeWinnerMessage((payload: ArcadeWinnerMessageResponse) => this.emitArcadeWinnerEvent(payload))
    );
    this.arcadeChatServerService.connect();
  }

  private async initialiseArcadeChatServerService(): Promise<void> {
    this.arcadeChatServerService = await this.serverServiceFactory.createChatService('arcade');
  }

  private async emitArcadeWinnerEvent(payload: ArcadeWinnerMessageResponse): Promise<void> {
    // RS - We've been asked to disable the threshold, adding as a feature toggle
    // incase they change their mind - could create a lot of spam..

    if (this.chatModConfig.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_skin_name_in_gamename_islive) !== 'true') {
      payload.skinname = null;
    }

    if (!this.isArcadeThresholdRemovalLive()) {
      const prizeThreshold = this.getArcadeTopPrizeThreshold();
      if (prizeThreshold && payload.prize < prizeThreshold) {
        return;
      }
    }

    this.eventBusService.getEventBus().$emit(ArcadeWinnerReceivedEventName, payload);
  }

  private isArcadeThresholdRemovalLive(): boolean {
    return this.chatModConfig.getConfigurationByPath(ConfigurationPaths.featuretoggle.arcade_500pound_alert_threshold_removal_islive) === 'true';
  }

  private getArcadeTopPrizeThreshold(): number | undefined {
    const prizeThreshold = parseInt(this.chatModConfig.getConfigurationByPath(ConfigurationPaths.prizes.arcade_top_prize_threshold), 10);
    if (!prizeThreshold || isNaN(prizeThreshold)) {
      return;
    }

    return prizeThreshold;
  }

  private connectAndAuthToChat() {
    const user = this.getCurrentUser();
    const userId: number = user.userId;
    const crossCheck: number = user.crosscheckId;
    const roomId: number = this.configurationService.getConfigurationItem<number>(ConfigurationKey.ARCADE_MSG_ROOM_ID);
    const clientTypeId: number = this.configurationService.getConfigurationItem<number>(ConfigurationKey.CLIENT_TYPE_ID);
    const stealthOnEnter: boolean = true;
    const authMessage = {
      toPayload() {
        return `<admin command="auth" roomId="${roomId}" id="${userId}" clientTypeId="${clientTypeId}" crossCheck="${crossCheck}" stealth="${stealthOnEnter}"/>`;
      }
    };
    this.arcadeChatServerService.send(authMessage);
  }

  private getCurrentUser(): User {
    return this.persistedStore.Get(User) as User;
  }

  public getAdminFeedService(): IChatServerService {
    return this.arcadeChatServerService;
  }

  public dispose(): void {
    if (this.subscriptionStore) {
      this.subscriptionStore.unsubscribeAll();
      this.subscriptionStore = null;
    }

    if (this.arcadeChatServerService) {
      const force = false;
      this.arcadeChatServerService.disconnect(force);
      this.arcadeChatServerService = null;
    }
  }
}

export { IArcadeAdminFeedServiceProvider, ArcadeAdminFeedServiceProvider };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/arcade-admin-feed-service-provider/arcade-admin-feed-service-provider.ts